import React, { useState, useCallback, useRef } from "react";

const IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];

const IMAGE_OUTPUTS = [
    { value: "image/png", label: "PNG" },
    { value: "image/jpeg", label: "JPG" },
    { value: "image/webp", label: "WEBP" },
];

const HEIC_OUTPUTS = [
    { value: "image/jpeg", label: "JPG" },
    { value: "image/png", label: "PNG" },
    { value: "image/webp", label: "WEBP" },
];

function detectKind(file) {
    if (!file) return null;
    const name = file.name.toLowerCase();

    const isHeic =
        name.endsWith(".heic") ||
        name.endsWith(".heif") ||
        file.type === "image/heic" ||
        file.type === "image/heif";
    if (isHeic) return "heic";

    if (IMAGE_MIME_TYPES.includes(file.type)) return "image";
    if (file.type === "application/pdf") return "pdf";

    if (
        name.endsWith(".docx") ||
        file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) return "docx";

    if (
        name.endsWith(".xlsx") ||
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) return "xlsx";

    return "unsupported";
}

function getOutputOptions(kind, file) {
    if (kind === "image") {
        return [
            ...IMAGE_OUTPUTS.filter((o) => o.value !== file.type),
            { value: "pdf", label: "PDF" },
        ];
    }

    if (kind === "heic") {
        return HEIC_OUTPUTS;
    }

    if (kind === "pdf") {
        return [{ value: "png-pages", label: "PNG (one image per page)" }];
    }

    if (kind === "docx") {
        return [{ value: "html", label: "HTML / Text" }];
    }

    if (kind === "xlsx") {
        return [
            { value: "pdf", label: "PDF Document (.pdf)" },
            { value: "png-pages", label: "PNG Images (.png)" },
            { value: "html-table", label: "HTML Web Table (.html)" }
        ];
    }

    return [];
}

function downloadBlob(blob, name) {
    const url = URL.createObjectURL(blob);
    let previewKind = "file";
    if (blob.type.startsWith("image/")) previewKind = "image";
    else if (blob.type === "text/html") previewKind = "html";
    else if (blob.type === "application/pdf") previewKind = "pdf";
    return { name, url, previewKind, mime: blob.type };
}

function loadImage(fileOrBlob) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = URL.createObjectURL(fileOrBlob);
    });
}

async function convertImageToImage(file, mime) {
    const img = await loadImage(file);
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");

    if (mime === "image/jpeg") {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(img, 0, 0);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, mime, 0.92));
    const ext = mime.split("/")[1];
    const base = file.name.replace(/\.[^/.]+$/, "");
    return [downloadBlob(blob, `${base}.${ext}`)];
}

async function convertImageToPdf(file) {
    const { jsPDF } = await import("jspdf");
    const img = await loadImage(file);

    const orientation = img.width > img.height ? "landscape" : "portrait";
    const pdf = new jsPDF({ orientation, unit: "px", format: [img.width, img.height] });

    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.95);

    pdf.addImage(dataUrl, "JPEG", 0, 0, img.width, img.height);
    const blob = pdf.output("blob");
    const base = file.name.replace(/\.[^/.]+$/, "");
    return [downloadBlob(blob, `${base}.pdf`)];
}

// Some browsers (notably Safari, and some Chromium builds on macOS/iOS) can
// decode certain HEIC variants natively through <img>/canvas without any
// JS library at all. We try this first — it's free and handles cases
// heic2any's bundled libheif.js chokes on (10-bit/HDR HEIC, some Android
// HEIC encoders, burst/multi-image containers).
async function tryNativeHeicDecode(file) {
    try {
        const img = await loadImage(file);
        // A "successful" decode of an unsupported format can still produce a
        // 0x0 image in some browsers instead of throwing — treat that as a
        // failure so we fall through to heic2any.
        if (!img.width || !img.height) throw new Error("native decode produced empty image");
        return img;
    } catch {
        return null;
    }
}

function reencodeImageEl(img, mime, quality = 0.92) {
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");

    if (mime === "image/jpeg") {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(img, 0, 0);

    return new Promise((resolve) => canvas.toBlob(resolve, mime, quality));
}

// heic2any bundles a lightweight libheif.js WASM build that's only compiled
// for the common case: 4:2:0 chroma, 8-bit depth. Some HEIC files — HDR/10-bit
// photos, certain Android encoders, edited/burst photos — use 4:2:2, 4:4:4, or
// >8-bit encoding, which that slim build rejects with
// "ERR_LIBHEIF format not supported". libheif-js's `/wasm-bundle` entrypoint
// ships the FULL libheif WASM build (extended chroma + bit-depth range
// extensions) and can decode those variants. It's heavier, so we only load it
// as a last-resort fallback.
async function decodeWithLibheifFull(file) {
    const mod = await import("libheif-js/wasm-bundle");
    const libheif = mod.default ?? mod;

    const buffer = await file.arrayBuffer();
    const decoder = new libheif.HeifDecoder();
    const images = decoder.decode(buffer);

    if (!images || !images.length) {
        throw new Error("No image data found in this HEIC file.");
    }

    // If the file has multiple images (e.g. a Live Photo/burst container),
    // we take the first — that's the primary photo in virtually all cases.
    const image = images[0];
    const width = image.get_width();
    const height = image.get_height();

    const displayData = await new Promise((resolve, reject) => {
        image.display(
            { data: new Uint8ClampedArray(width * height * 4), width, height },
            (result) => {
                if (!result) {
                    reject(new Error("libheif-js failed to render this HEIC image."));
                    return;
                }
                resolve(result);
            }
        );
    });

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.putImageData(new ImageData(new Uint8ClampedArray(displayData.data), width, height), 0, 0);
    return canvas;
}

// Decodes a HEIC/HEIF photo (the default format for recent iPhone photos) into
// a normal browser-readable image, then optionally re-encodes it into the
// requested output format. Tries three tiers, cheapest/most-limited first:
//   1) native <img> decode (free, works in Safari for some variants)
//   2) heic2any (small WASM build, handles standard 8-bit 4:2:0 HEIC — most iPhone photos)
//   3) libheif-js full WASM build (handles HDR/10-bit, 4:2:2/4:4:4 chroma, etc.)
async function convertHeicToImage(file, mime) {
    const base = file.name.replace(/\.[^/.]+$/, "");
    const ext = mime.split("/")[1];

    // 1) Native browser decode.
    const nativeImg = await tryNativeHeicDecode(file);
    if (nativeImg) {
        const blob = await reencodeImageEl(nativeImg, mime === "image/png" ? "image/png" : mime, 0.92);
        return [downloadBlob(blob, `${base}.${ext}`)];
    }

    // 2) heic2any (fast path for the common case).
    let decodedBlob = null;
    let heic2anyError = null;
    try {
        const heic2anyModule = await import("heic2any");
        const heic2any = heic2anyModule.default ?? heic2anyModule;
        const decoded = await heic2any({ blob: file, toType: "image/png", quality: 0.92 });
        decodedBlob = Array.isArray(decoded) ? decoded[0] : decoded;
    } catch (err) {
        heic2anyError = err;
    }

    if (decodedBlob) {
        if (mime === "image/png") {
            return [downloadBlob(decodedBlob, `${base}.png`)];
        }
        const img = await loadImage(decodedBlob);
        const blob = await reencodeImageEl(img, mime, 0.92);
        return [downloadBlob(blob, `${base}.${ext}`)];
    }

    // 3) libheif-js full WASM build — handles the format variants tier 2 rejected.
    try {
        const canvas = await decodeWithLibheifFull(file);
        const outMime = mime === "image/png" ? "image/png" : mime;
        const blob = await new Promise((resolve) => {
            if (outMime === "image/jpeg") {
                // Flatten onto white first (JPEG has no alpha channel).
                const flat = document.createElement("canvas");
                flat.width = canvas.width;
                flat.height = canvas.height;
                const fctx = flat.getContext("2d");
                fctx.fillStyle = "#ffffff";
                fctx.fillRect(0, 0, flat.width, flat.height);
                fctx.drawImage(canvas, 0, 0);
                flat.toBlob(resolve, outMime, 0.92);
            } else {
                canvas.toBlob(resolve, outMime, 0.92);
            }
        });
        return [downloadBlob(blob, `${base}.${ext}`)];
    } catch (fullDecodeErr) {
        const reason =
            heic2anyError?.message ||
            heic2anyError?.code ||
            fullDecodeErr?.message ||
            "unknown decoder error";
        throw new Error(
            `This HEIC file couldn't be decoded by any in-browser decoder (${reason}). ` +
            `It may be corrupted, DRM-protected, or use a HEIC profile no web decoder currently supports. ` +
            `Try re-exporting it as JPEG from your phone's Photos app, or use "Settings > Camera > Formats > Most Compatible" on iPhone for future photos.`
        );
    }
}

async function convertPdfToImages(file) {
    const pdfjsLib = await import("pdfjs-dist");
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/build/pdf.worker.min.mjs",
        import.meta.url
    ).toString();

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const results = [];
    const base = file.name.replace(/\.[^/.]+$/, "");

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d");

        await page.render({ canvasContext: ctx, viewport }).promise;

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
        results.push(downloadBlob(blob, `${base}-page-${pageNum}.png`));
    }

    return results;
}

async function convertDocxToHtml(file) {
    const mammoth = await import("mammoth");
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.convertToHtml({ arrayBuffer });

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${file.name}</title></head>
<body>${result.value}</body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const base = file.name.replace(/\.[^/.]+$/, "");
    const out = downloadBlob(blob, `${base}.html`);
    return [{ ...out, htmlContent: result.value }];
}

async function renderWorkbookToCanvases(file) {
    const { read, utils } = await import("xlsx");
    const arrayBuffer = await file.arrayBuffer();
    const workbook = read(arrayBuffer, { type: "array" });

    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-9999px";
    container.style.top = "-9999px";
    container.style.width = "1000px";
    document.body.appendChild(container);

    const canvases = [];

    for (const sheetName of workbook.SheetNames) {
        const worksheet = workbook.Sheets[sheetName];
        const rawHtmlTable = utils.sheet_to_html(worksheet);

        const sheetWrapper = document.createElement("div");
        sheetWrapper.style.background = "#ffffff";
        sheetWrapper.style.padding = "30px";
        sheetWrapper.style.fontFamily = "system-ui, -apple-system, sans-serif";

        sheetWrapper.innerHTML = `
            <h2 style="color: #0B3D2E; margin-top: 0; margin-bottom: 16px; font-size: 20px; border-bottom: 2px solid #2F8F6F; padding-bottom: 6px;">
                📊 Sheet: ${sheetName}
            </h2>
            <style>
                table { border-collapse: collapse; width: 100%; font-size: 13px; background: white; }
                th, td { border: 1px solid #cbd5e1; padding: 8px 10px; text-align: left; }
                th { background-color: #f1f5f9; font-weight: 600; color: #1e293b; }
                tr:nth-child(even) { background-color: #f8fafc; }
            </style>
            ${rawHtmlTable}
        `;

        container.appendChild(sheetWrapper);

        const { default: html2canvas } = await import("html2canvas");
        const canvas = await html2canvas(sheetWrapper, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff"
        });

        canvases.push({ canvas, sheetName });
        container.removeChild(sheetWrapper);
    }

    document.body.removeChild(container);
    return canvases;
}

async function convertXlsxToHtml(file) {
    const { read, utils } = await import("xlsx");
    const arrayBuffer = await file.arrayBuffer();
    const workbook = read(arrayBuffer, { type: "array" });

    let combinedHtml = "";
    workbook.SheetNames.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];
        const rawHtmlTable = utils.sheet_to_html(worksheet);
        combinedHtml += `
            <div style="margin-bottom: 2.5rem;">
                <h3 style="color: #145A43; margin-bottom: 0.5rem; font-weight: bold; border-bottom: 2px solid #2F8F6F; padding-bottom: 0.25rem;">
                    📊 Sheet: ${sheetName}
                </h3>
                <div style="overflow-x: auto; max-width: 100%;">
                    ${rawHtmlTable}
                </div>
            </div>
        `;
    });

    const tableStyles = `
        <style>
            table { border-collapse: collapse; width: 100%; font-size: 13px; margin: 10px 0; background: white; }
            th, td { border: 1px solid #e2e8f0; padding: 8px 12px; text-align: left; min-width: 80px; }
            th { background-color: #f8fafc; font-weight: 600; color: #334155; }
            tr:nth-child(even) { background-color: #f8fafc; }
        </style>
    `;

    const fullHtmlPage = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${file.name}</title>${tableStyles}</head>
<body style="font-family: system-ui, sans-serif; padding: 20px; color: #334155; background: #f1f5f9;">${combinedHtml}</body></html>`;

    const blob = new Blob([fullHtmlPage], { type: "text/html" });
    const base = file.name.replace(/\.[^/.]+$/, "");
    const out = downloadBlob(blob, `${base}.html`);
    return [{ ...out, htmlContent: combinedHtml }];
}

async function convertXlsxToImages(file) {
    const canvasesData = await renderWorkbookToCanvases(file);
    const base = file.name.replace(/\.[^/.]+$/, "");

    return await Promise.all(
        canvasesData.map(async ({ canvas, sheetName }) => {
            const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
            return downloadBlob(blob, `${base}-${sheetName.replace(/\s+/g, "_")}.png`);
        })
    );
}

async function convertXlsxToPdf(file) {
    const { jsPDF } = await import("jspdf");
    const canvasesData = await renderWorkbookToCanvases(file);
    const base = file.name.replace(/\.[^/.]+$/, "");

    let pdf = null;

    for (let i = 0; i < canvasesData.length; i++) {
        const { canvas } = canvasesData[i];
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const orientation = imgWidth > imgHeight ? "l" : "p";

        if (i === 0) {
            pdf = new jsPDF({ orientation, unit: "px", format: [imgWidth, imgHeight] });
        } else {
            pdf.addPage([imgWidth, imgHeight], orientation);
        }

        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        pdf.addImage(dataUrl, "JPEG", 0, 0, imgWidth, imgHeight);
    }

    const blob = pdf.output("blob");
    return [downloadBlob(blob, `${base}.pdf`)];
}

async function convertOneFile(item) {
    const { kind, file, target } = item;

    if (kind === "heic") return convertHeicToImage(file, target);
    if (kind === "image" && target === "pdf") return convertImageToPdf(file);
    if (kind === "image") return convertImageToImage(file, target);
    if (kind === "pdf" && target === "png-pages") return convertPdfToImages(file);
    if (kind === "docx" && target === "html") return convertDocxToHtml(file);
    if (kind === "xlsx" && target === "html-table") return convertXlsxToHtml(file);
    if (kind === "xlsx" && target === "png-pages") return convertXlsxToImages(file);
    if (kind === "xlsx" && target === "pdf") return convertXlsxToPdf(file);

    throw new Error("Unsupported conversion.");
}

function triggerDownload(url, name) {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

function makeItem(file) {
    const kind = detectKind(file);
    const options = getOutputOptions(kind, file);
    return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        file,
        kind,
        target: options[0]?.value ?? null,
        status: "idle", // idle | working | done | error
        errorMsg: "",
        outputs: [],
    };
}

export default function FileConverter() {
    const [items, setItems] = useState([]);
    const [isConvertingAll, setIsConvertingAll] = useState(false);
    const [skippedNames, setSkippedNames] = useState([]);

    const inputRef = useRef(null);
    const addMoreRef = useRef(null);

    const updateItem = (id, updates) => {
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...updates } : it)));
    };

    const removeItem = (id) => {
        setItems((prev) => {
            const target = prev.find((it) => it.id === id);
            target?.outputs.forEach((o) => URL.revokeObjectURL(o.url));
            return prev.filter((it) => it.id !== id);
        });
    };

    const addFiles = (fileList) => {
        const files = Array.from(fileList ?? []);
        if (!files.length) return;

        const accepted = [];
        const skipped = [];

        files.forEach((file) => {
            const kind = detectKind(file);
            if (kind === "unsupported") {
                skipped.push(file.name);
            } else {
                accepted.push(makeItem(file));
            }
        });

        setSkippedNames(skipped);
        if (accepted.length) {
            setItems((prev) => [...prev, ...accepted]);
        }
    };

    const onFileChange = (e) => {
        addFiles(e.target.files);
        e.target.value = "";
    };

    const convertOne = useCallback(async (item) => {
        updateItem(item.id, { status: "working", errorMsg: "" });
        try {
            const results = await convertOneFile(item);
            updateItem(item.id, { status: "done", outputs: results });
        } catch (err) {
            console.error(err);
            // Fix: surface the real reason (e.g. HEIC decode failure detail)
            // instead of always showing the same generic message.
            const message = err?.message?.trim()
                ? err.message
                : "Conversion failed. Check that this file matches the selected conversion type.";
            updateItem(item.id, {
                status: "error",
                errorMsg: message,
            });
        }
    }, []);

    const handleConvertAll = useCallback(async () => {
        setIsConvertingAll(true);
        // Snapshot the queue up front; conversions run one at a time so we
        // don't spin up several heavy PDF/canvas renders simultaneously.
        const queue = items.filter((it) => it.status !== "done" && it.target);
        for (const item of queue) {
            await convertOne(item);
        }
        setIsConvertingAll(false);
    }, [items, convertOne]);

    const handleDownloadAll = useCallback(async () => {
        const allOutputs = items.flatMap((it) => it.outputs);
        for (const out of allOutputs) {
            triggerDownload(out.url, out.name);
            // Small delay between triggers — browsers can silently block a
            // burst of simultaneous downloads.
            await new Promise((resolve) => setTimeout(resolve, 250));
        }
    }, [items]);

    const resetAll = () => {
        items.forEach((it) => it.outputs.forEach((o) => URL.revokeObjectURL(o.url)));
        setItems([]);
        setSkippedNames([]);
    };

    const doneCount = items.filter((it) => it.status === "done").length;
    const pendingCount = items.filter((it) => it.status !== "done" && it.target).length;
    const totalOutputs = items.reduce((sum, it) => sum + it.outputs.length, 0);
    const showUploadZone = items.length === 0;

    return (
        <div className="
            min-h-screen
            bg-[#0F0F0F]
            flex
            text-white
            relative
            overflow-hidden
            flex-col
        ">
            <div className="absolute top-10 left-4 sm:top-20 sm:left-20 w-48 h-48 sm:w-80 sm:h-80 bg-[#0B3D2E]/30 blur-[100px] sm:blur-[140px] rounded-full" />
            <div className="absolute bottom-4 right-4 sm:bottom-10 sm:right-10 w-48 h-48 sm:w-80 sm:h-80 bg-[#145A43]/20 blur-[100px] sm:blur-[140px] rounded-full" />

            {/* ── Header ── */}
            <div className="bg-gradient-to-br from-[#0B3D2E] via-[#145A43] to-[#0B3D2E] border-b border-[#2F8F6F]/20 px-6 py-12 md:py-16 shadow-lg">
                <div className="max-w-2xl mx-auto flex flex-col justify-center items-center text-center space-y-3">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-white to-[#D7C7A1]">
                        Universal File Converter
                    </h1>
                    <p className="text-[#D7C7A1] text-xs sm:text-sm max-w-md font-medium leading-relaxed">
                        Convert images, PDFs, HEIC photos & documents instantly.
                        <span className="block sm:inline"> 100% private, processed inside your browser.</span>
                    </p>
                </div>
            </div>

            {/* ── Main Container ── */}
            <div className="
                relative z-10
                w-auto max-w-3xl
                mx-4 sm:mx-auto my-8
                rounded-2xl sm:rounded-[32px]
                bg-white/5
                backdrop-blur-2xl
                border border-[#2F8F6F]/15
                p-4 sm:p-8 md:p-10
            ">
                <div className="space-y-4 sm:space-y-6">

                    <Step number={1} title="Upload your files" active>
                        <input
                            ref={inputRef}
                            type="file"
                            multiple
                            accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,.docx,.xlsx,.heic,.heif,image/heic,image/heif"
                            onChange={onFileChange}
                            className="hidden"
                        />

                        <div
                            onClick={() => inputRef.current?.click()}
                            className="
                                h-32 sm:h-40
                                rounded-2xl sm:rounded-3xl
                                border-2 border-dashed
                                border-[#2F8F6F]/30
                                bg-[#0B3D2E]/10
                                flex flex-col
                                items-center
                                justify-center
                                cursor-pointer
                                transition
                                px-3
                                text-center
                                hover:bg-[#0B3D2E]/20
                                hover:border-[#2F8F6F]/60
                                hover:shadow-[0_0_40px_rgba(47,143,111,0.2)]
                            "
                        >
                            <div className="text-4xl sm:text-5xl mb-2 sm:mb-3">📂</div>

                            {items.length > 0 ? (
                                <>
                                    <p className="font-medium text-[#D7C7A1] text-sm sm:text-base">
                                        {items.length} file{items.length > 1 ? "s" : ""} selected
                                    </p>
                                    <p className="text-xs text-[#2F8F6F]/60 mt-1">
                                        Click to add more files
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className="font-medium text-sm sm:text-base text-white">Click to select files</p>
                                    <p className="text-xs sm:text-sm text-[#D7C7A1]/50">
                                        Images, HEIC, PDF, .docx, or .xlsx — select multiple
                                    </p>
                                </>
                            )}
                        </div>

                        {skippedNames.length > 0 && (
                            <div className="mt-3 text-xs text-yellow-300/80 bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-3">
                                Skipped unsupported file{skippedNames.length > 1 ? "s" : ""}: {skippedNames.join(", ")}
                            </div>
                        )}
                    </Step>

                    <Step
                        number={2}
                        title="Choose output for each file & convert"
                        active={items.length > 0}
                        disabled={items.length === 0}
                    >
                        {items.length === 0 ? (
                            <p className="text-sm text-[#2F8F6F]/50 px-1">
                                Upload files first to see conversion options.
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {items.map((item) => (
                                    <FileItemCard
                                        key={item.id}
                                        item={item}
                                        onTargetChange={(value) => updateItem(item.id, { target: value })}
                                        onConvert={() => convertOne(item)}
                                        onRemove={() => removeItem(item.id)}
                                        onDownloadAll={async () => {
                                            for (const out of item.outputs) {
                                                triggerDownload(out.url, out.name);
                                                await new Promise((r) => setTimeout(r, 200));
                                            }
                                        }}
                                        disabled={isConvertingAll}
                                    />
                                ))}

                                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                                    <input
                                        ref={addMoreRef}
                                        type="file"
                                        multiple
                                        accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,.docx,.xlsx,.heic,.heif,image/heic,image/heif"
                                        onChange={onFileChange}
                                        className="hidden"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => addMoreRef.current?.click()}
                                        disabled={isConvertingAll}
                                        className="flex-1 text-xs sm:text-sm py-2.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition disabled:opacity-50"
                                    >
                                        ➕ Add more files
                                    </button>
                                    <button
                                        type="button"
                                        onClick={resetAll}
                                        disabled={isConvertingAll}
                                        className="flex-1 text-xs sm:text-sm py-2.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition disabled:opacity-50"
                                    >
                                        🗑️ Clear all
                                    </button>
                                </div>
                            </div>
                        )}
                    </Step>

                    <Step
                        number={3}
                        title="Convert all and download"
                        active={items.length > 0}
                        disabled={items.length === 0}
                    >
                        <button
                            onClick={handleConvertAll}
                            disabled={items.length === 0 || pendingCount === 0 || isConvertingAll}
                            className="
                                w-full py-3 sm:py-4
                                rounded-full
                                font-bold
                                text-sm sm:text-base
                                bg-gradient-to-r
                                from-[#2F8F6F]
                                to-[#145A43]
                                shadow-lg shadow-[#2F8F6F]/30
                                hover:scale-[1.02]
                                transition
                                disabled:opacity-40
                                disabled:hover:scale-100
                                text-white
                            "
                        >
                            {isConvertingAll
                                ? "✨ Converting your files..."
                                : pendingCount > 0
                                    ? `⚡ Convert ${pendingCount} File${pendingCount > 1 ? "s" : ""}`
                                    : "✅ All files converted"}
                        </button>

                        {doneCount > 0 && (
                            <button
                                onClick={handleDownloadAll}
                                disabled={isConvertingAll || totalOutputs === 0}
                                className="
                                    w-full mt-3
                                    py-2.5 sm:py-3
                                    rounded-full
                                    font-semibold
                                    text-sm
                                    bg-white/5
                                    border border-[#2F8F6F]/30
                                    hover:bg-white/10
                                    transition
                                    disabled:opacity-40
                                    text-white
                                "
                            >
                                📥 Download All ({totalOutputs} file{totalOutputs !== 1 ? "s" : ""})
                            </button>
                        )}

                        <p className="text-xs text-[#2F8F6F]/40 mt-3 text-center">
                            If a download doesn't start, your browser may have blocked
                            it — check the address bar for a permission prompt.
                        </p>
                    </Step>

                </div>
            </div>

            <div className="text-gray-400 flex w-full justify-center text-xs md:text-md text-end bg-[#0F0F0F] pb-10">
                <p>Copyright © 2025 Rizki Makruf. All rights reserved.</p>
            </div>
        </div>
    );
}

function FileItemCard({ item, onTargetChange, onConvert, onRemove, onDownloadAll, disabled }) {
    const outputOptions = getOutputOptions(item.kind, item.file);
    const kindLabel = {
        image: "Image",
        heic: "HEIC Photo",
        pdf: "PDF",
        docx: "Word Doc",
        xlsx: "Spreadsheet",
    }[item.kind] ?? "File";

    return (
        <div className="rounded-2xl border border-[#2F8F6F]/20 bg-[#0B3D2E]/15 p-3 sm:p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#D7C7A1] truncate">{item.file.name}</p>
                    <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wide text-[#2F8F6F] bg-[#2F8F6F]/15 rounded-full px-2 py-0.5">
                        {kindLabel}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={onRemove}
                    disabled={disabled}
                    className="shrink-0 text-red-300/70 hover:text-red-200 text-xs disabled:opacity-40"
                >
                    Remove
                </button>
            </div>

            {outputOptions.length > 0 ? (
                <div className="flex flex-col sm:flex-row gap-2">
                    <select
                        value={item.target ?? ""}
                        onChange={(e) => onTargetChange(e.target.value)}
                        disabled={disabled || item.status === "working"}
                        className="
                            flex-1
                            rounded-xl
                            bg-[#0B3D2E]/30
                            border border-[#2F8F6F]/30
                            px-3 py-2
                            text-sm
                            text-white
                            outline-none
                            disabled:opacity-50
                        "
                    >
                        {outputOptions.map((opt) => (
                            <option key={opt.value} value={opt.value} className="bg-[#0B3D2E]">
                                {opt.label}
                            </option>
                        ))}
                    </select>

                    {item.status !== "done" && (
                        <button
                            type="button"
                            onClick={onConvert}
                            disabled={disabled || item.status === "working"}
                            className="
                                shrink-0
                                rounded-xl
                                bg-[#2F8F6F]/80
                                hover:bg-[#2F8F6F]
                                px-4 py-2
                                text-sm font-semibold
                                text-white
                                transition
                                disabled:opacity-50
                            "
                        >
                            {item.status === "working" ? "Converting…" : "Convert"}
                        </button>
                    )}
                </div>
            ) : (
                <p className="text-xs text-red-300/70">No supported conversion for this file type.</p>
            )}

            {item.status === "error" && (
                <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl p-2.5">
                    {item.errorMsg}
                </div>
            )}

            {item.status === "done" && item.outputs.length > 0 && (
                <div className="space-y-2 pt-1">
                    {item.outputs.map((out, i) => (
                        <div key={i} className="flex items-center justify-between gap-2 bg-[#2F8F6F]/10 border border-[#2F8F6F]/20 rounded-xl px-3 py-2">
                            <span className="truncate text-xs text-[#D7C7A1]">{out.name}</span>
                            <button
                                type="button"
                                onClick={() => triggerDownload(out.url, out.name)}
                                className="shrink-0 text-xs font-semibold text-white bg-gradient-to-r from-[#2F8F6F] to-[#145A43] rounded-lg px-3 py-1.5 hover:scale-[1.03] transition"
                            >
                                📥 Download
                            </button>
                        </div>
                    ))}
                    {item.outputs.length > 1 && (
                        <button
                            type="button"
                            onClick={onDownloadAll}
                            className="w-full text-xs font-semibold text-[#D7C7A1] border border-[#2F8F6F]/30 rounded-lg py-1.5 hover:bg-white/5 transition"
                        >
                            Download all {item.outputs.length} outputs
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function Step({ number, title, children, active, disabled }) {
    return (
        <div
            className={`
                rounded-2xl sm:rounded-3xl
                border
                p-3 sm:p-5
                transition
                ${active ? "border-[#2F8F6F]/25 bg-[#0B3D2E]/15" : "border-white/5 bg-white/[0.02]"}
                ${disabled ? "opacity-50" : ""}
            `}
        >
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div
                    className={`
                        w-7 h-7 sm:w-8 sm:h-8 rounded-full
                        flex items-center justify-center
                        text-xs sm:text-sm font-bold
                        shrink-0
                        ${active
                            ? "bg-[#2F8F6F] text-white"
                            : "bg-white/10 text-[#2F8F6F]/40"
                        }
                    `}
                >
                    {number}
                </div>
                <p className="text-xs sm:text-sm font-semibold text-[#D7C7A1]/70">
                    ✨ Step {number} — {title}
                </p>
            </div>
            {children}
        </div>
    );
}